'use client';

import { ShareIos } from '@byway/icons';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { DialogTrigger, Button as RACButton } from 'react-aria-components';
import {
  EmailIcon,
  EmailShareButton,
  FacebookIcon,
  FacebookShareButton,
  LinkedinIcon,
  LinkedinShareButton,
  RedditIcon,
  RedditShareButton,
  TelegramIcon,
  TelegramShareButton,
  TwitterShareButton,
  WhatsappIcon,
  WhatsappShareButton,
  XIcon
} from 'react-share';
import { Drawer } from '~/components/Drawer';
import { Popover } from '~/components/Popover';
import { trackEvent } from '~/utils';
import styles from './ShareTripButton.module.css';

const SHARE_TRACKING_ID = 'share-button';

const trackShare = () => {
  trackEvent(SHARE_TRACKING_ID, { event_category: 'click', event_label: SHARE_TRACKING_ID });
};

const SHARE_PROVIDERS = [
  { Component: EmailShareButton, name: 'Email', Icon: EmailIcon },
  { Component: WhatsappShareButton, name: 'WhatsApp', Icon: WhatsappIcon },
  { Component: FacebookShareButton, name: 'Facebook', Icon: FacebookIcon },
  { Component: TwitterShareButton, name: 'Twitter', Icon: XIcon },
  { Component: LinkedinShareButton, name: 'LinkedIn', Icon: LinkedinIcon },
  { Component: TelegramShareButton, name: 'Telegram', Icon: TelegramIcon },
  { Component: RedditShareButton, name: 'Reddit', Icon: RedditIcon }
];

type ShareTripButtonProps = {
  className: string;
  size: 'small' | 'large';
  presentation?: 'popover' | 'drawer';
  iconVariant?: 'default' | 'overviewIntro';
};

const ShareProvidersList = ({ url }: Readonly<{ url: string }>) => (
  <ul className={styles.shareProviders}>
    {SHARE_PROVIDERS.map((Provider) => (
      <li key={Provider.name} className={styles.shareProvider}>
        <Provider.Component
          url={url}
          resetButtonStyle={false}
          className={styles.providerButton}
          role="button"
          tabIndex={0}
        >
          <Provider.Icon size={32} round className={styles.providerIcon} />
          <span>{Provider.name}</span>
        </Provider.Component>
      </li>
    ))}
  </ul>
);

export const ShareTripButton = ({
  className,
  size,
  presentation = 'popover',
  iconVariant = 'default'
}: Readonly<ShareTripButtonProps>) => {
  const t = useTranslations('journey');
  const [url, setUrl] = useState('');
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const iconSize = iconVariant === 'overviewIntro' ? 'small' : size;

  useEffect(() => {
    setUrl(window.location.href);
  }, []);

  if (presentation === 'drawer') {
    return (
      <>
        <button
          type="button"
          className={className}
          aria-label={t('overview_share_trip')}
          onClick={() => {
            trackShare();
            setIsDrawerOpen(true);
          }}
        >
          <ShareIos decorative isInline size={iconSize} />
        </button>
        <Drawer
          isOpen={isDrawerOpen}
          close={() => setIsDrawerOpen(false)}
          title={t('overview_share_trip')}
          ariaLabel={t('overview_share_trip')}
          dataTestId="ShareTripDrawer"
        >
          <ShareProvidersList url={url} />
        </Drawer>
      </>
    );
  }

  return (
    <DialogTrigger>
      <RACButton className={className} aria-label={t('overview_share_trip')} onPress={trackShare}>
        <ShareIos decorative isInline size={iconSize} />
      </RACButton>
      <Popover>
        <ShareProvidersList url={url} />
      </Popover>
    </DialogTrigger>
  );
};
